/** Optional flight fares; a total remains mandatory on saved segments. */
export type FlightPricingInput = {
  cost?: unknown;
  costForAdult?: unknown;
  costForChild?: unknown;
  costForInfant?: unknown;
  connections?: unknown;
};

export type FlightTravelerCounts = { currency?: unknown; adults?: unknown; cwb?: unknown; cnb?: unknown; infant?: unknown };
export const FLIGHT_RATE_FIELDS = ["costForAdult", "costForChild", "costForInfant"] as const;
const present = (value: unknown): boolean => value !== undefined && value !== null && value !== "";
const number = (value: unknown): number => Number.isFinite(Number(value)) ? Number(value) : 0;
const rows = (value: unknown): FlightPricingInput[] => Array.isArray(value) ? value.filter((row) => row && typeof row === "object" && !Array.isArray(row)) : [];

/** Rounds flight totals to cents, stabilizing floating-point arithmetic. */
export const roundFlightCost = (value: number, precision = 2): number => Math.round((value + Number.EPSILON * Math.max(1, Math.abs(value))) * 10 ** precision) / 10 ** precision;
const farePrecision = (counts: FlightTravelerCounts): number => new Intl.NumberFormat("en", { style: "currency", currency: typeof counts.currency === "string" && /^[A-Za-z]{3}$/.test(counts.currency) ? counts.currency.toUpperCase() : "INR" }).resolvedOptions().maximumFractionDigits ?? 2;

/** Distinguishes omitted fares from an explicitly entered zero fare. */
export const hasFlightPassengerRates = (flight: FlightPricingInput): boolean => FLIGHT_RATE_FIELDS.some((field) => present(flight[field]));

/** Calculates the segment total from optional fares; blank fares contribute zero. */
export const calculateFlightPassengerTotal = (flight: FlightPricingInput, counts: FlightTravelerCounts): number => roundFlightCost(
  number(flight.costForAdult) * number(counts.adults) +
  number(flight.costForChild) * (number(counts.cwb) + number(counts.cnb)) +
  number(flight.costForInfant) * number(counts.infant), farePrecision(counts),
);

/** Totals mandatory segment costs, including connections, without replacing user-entered totals. */
export const calculateTravelTotal = (travel: unknown): number => rows(travel).reduce((sum, flight) => sum + number(flight.cost) + calculateTravelTotal(flight.connections), 0);

/** Allocates optional fares to their passenger groups and preserves total-only flight allocation. */
export const splitFlightCost = (flight: FlightPricingInput, counts: FlightTravelerCounts) => {
  if (hasFlightPassengerRates(flight)) return {
    adult: number(flight.costForAdult) * number(counts.adults),
    cwb: number(flight.costForChild) * number(counts.cwb),
    cnb: number(flight.costForChild) * number(counts.cnb),
    infant: number(flight.costForInfant) * number(counts.infant),
  };
  const chargeable = number(counts.adults) + number(counts.cwb) + number(counts.cnb);
  const share = chargeable > 0 ? number(flight.cost) / chargeable : 0;
  return { adult: share * number(counts.adults), cwb: share * number(counts.cwb), cnb: share * number(counts.cnb), infant: 0 };
};

/** Aggregates actual passenger allocations across every flight and connection. */
export const splitTravelCost = (travel: unknown, counts: FlightTravelerCounts): ReturnType<typeof splitFlightCost> => rows(travel).reduce((sum, flight) => {
  const main = splitFlightCost(flight, counts);
  const connections = splitTravelCost(flight.connections, counts);
  return { adult: sum.adult + main.adult + connections.adult, cwb: sum.cwb + main.cwb + connections.cwb,
    cnb: sum.cnb + main.cnb + connections.cnb, infant: sum.infant + main.infant + connections.infant };
}, { adult: 0, cwb: 0, cnb: 0, infant: 0 });

/** Validates one mandatory total and any optional fares, without silently correcting mismatches. */
export const flightCostError = (flight: FlightPricingInput, counts: FlightTravelerCounts): string | undefined => {
  if (!present(flight.cost) || typeof flight.cost === "boolean" || !Number.isFinite(Number(flight.cost)) || Number(flight.cost) < 0) return "Total cost is required and must be zero or greater";
  for (const field of FLIGHT_RATE_FIELDS) {
    const value = flight[field];
    if (present(value) && (typeof value === "boolean" || !Number.isFinite(Number(value)) || Number(value) < 0)) return "Passenger rates must be finite numbers zero or greater";
  }
  if (hasFlightPassengerRates(flight) && roundFlightCost(Number(flight.cost), farePrecision(counts)) !== calculateFlightPassengerTotal(flight, counts)) {
    return `Total cost must match passenger fares (${calculateFlightPassengerTotal(flight, counts).toFixed(2)})`;
  }
};

/** Checks all segments against the effective itinerary passenger counts, including partial updates. */
export const validateFlightCosts = (travel: unknown, counts: FlightTravelerCounts, prefix = "Flight"): string | undefined => {
  for (const [index, flight] of rows(travel).entries()) {
    const label = `${prefix} ${index + 1}`;
    const error = flightCostError(flight, counts) || validateFlightCosts(flight.connections, counts, `${label} connection`);
    if (error) return error.startsWith(label) ? error : `${label}: ${error}`;
  }
};
